// frontend/src/services/api.js
import axios from 'axios';
import { API_BASE_URL } from '../config/apiConfig';

// Create axios instance with correct base URL and API prefix
const api = axios.create({
    baseURL: `${API_BASE_URL}/api`,
    timeout: 30000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
    }
});

// Track refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    
    failedQueue = [];
};

// Helper function to check if token is expired
const isTokenExpired = (token) => {
    if (!token) return true;
    
    try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Date.now() / 1000;
        return payload.exp < now;
    } catch (e) {
        return true;
    }
};

// Request interceptor for adding JWT token and logging
api.interceptors.request.use(
    (config) => {
        // Add JWT token to requests if available and not expired
        const token = localStorage.getItem('scott_access_token');
        if (token && !isTokenExpired(token)) {
            config.headers['Authorization'] = `Bearer ${token}`;
        } else if (token && isTokenExpired(token)) {

            localStorage.removeItem('scott_access_token');
        }
        
        return config;
    },
    (error) => {
        console.error('[API REQUEST ERROR]', error);
        return Promise.reject(error);
    }
);

// Response interceptor for handling token refresh and errors
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        if (error.response) {
            // Skip logging if explicitly requested (e.g., for media loading)
            if (!originalRequest._skipErrorLogging) {
                console.error(`[API ERROR] ${error.response.status} `, {
                    url: error.config?.url,
                    status: error.response.status,
                    message: error.response.data?.error || error.response.data?.message,
                    data: error.response.data
                });
            }
            
            // Handle 401 errors - try to refresh token
            if (error.response.status === 401 && !originalRequest._retry) {
                // Don't retry for login or refresh endpoints
                if (originalRequest.url.includes('/auth/login') || 
                    originalRequest.url.includes('/auth/refresh')) {
                    return Promise.reject(error);
                }
                
                if (isRefreshing) {
                    // If already refreshing, queue this request
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then(token => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return api(originalRequest);
                    }).catch(err => {
                        return Promise.reject(err);
                    });
                }
                
                originalRequest._retry = true;
                isRefreshing = true;
                

                const refreshToken = localStorage.getItem('scott_refresh_token');
                
                if (!refreshToken) {
                    processQueue(new Error('No refresh token available'), null);
                    isRefreshing = false;
                    
                    // Clear auth data and redirect to login
                    localStorage.removeItem('scott_access_token');
                    localStorage.removeItem('scott_refresh_token');
                    localStorage.removeItem('currentUser');
                    
                    return Promise.reject(error);
                }
                
                try {
                    const refreshResponse = await axios.post(
                        `${API_BASE_URL}/api/auth/refresh`,
                        {
                            refresh_token: refreshToken
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json'
                            },
                            withCredentials: true,
                            timeout: 10000  // 10 second timeout for refresh requests
                        }
                    );
                    
                    if (refreshResponse.data) {
                        const newAccessToken = refreshResponse.data.access_token || 
                                             refreshResponse.data.tokens?.access_token;
                        const newRefreshToken = refreshResponse.data.refresh_token || 
                                               refreshResponse.data.tokens?.refresh_token ||
                                               refreshToken;
                        
                        if (newAccessToken) {
                            localStorage.setItem('scott_access_token', newAccessToken);
                            localStorage.setItem('scott_refresh_token', newRefreshToken);
                            
                            // Update user data if provided
                            if (refreshResponse.data.user) {
                                localStorage.setItem('currentUser', JSON.stringify(refreshResponse.data.user));
                            }
                            
                            // Update default header for future requests
                            api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                            
                            // Process queued requests
                            processQueue(null, newAccessToken);
                            
                            // Retry the original request with new token
                            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
                            return api(originalRequest);
                        }
                    }
                    
                    throw new Error('Invalid refresh response');
                    
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    
                    // Clear auth data and redirect to login
                    localStorage.removeItem('scott_access_token');
                    localStorage.removeItem('scott_refresh_token');
                    localStorage.removeItem('currentUser');
                    
                    console.error('Token refresh failed:', refreshError);
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }
        } else if (error.request) {
            console.error('[API ERROR] No response received', {
                url: error.config?.url,
                message: error.message
            });
        } else {
            console.error('[API ERROR] Request setup failed', error.message);
        }
        
        return Promise.reject(error);
    }
);

export default api;